/**
 * Managed User Profile Service
 *
 * Application service that loads a managed user's profile payload for manager
 * dashboard flows and maps it into the frontend summary model.
 *
 * Why this service exists
 * - Keeps feature hooks focused on UI/loading orchestration.
 * - Centralizes API orchestration + mapping in a reusable boundary.
 * - Provides a stable contract to manager-dashboard consumers.
 */

import { getManagerUserProfile } from '@/lib';
import { IManagedUserSummary } from '@/types';
import { mapApiManagedUserDataToFrontend } from '@/utils';

/**
 * Input contract for managed-user profile hydration.
 */
export interface LoadManagedUserProfileInput {
  /** Manager user UUID used by manager profile endpoints. */
  managerId: string;
  /** Managed user UUID for profile detail retrieval. */
  userId: string;
}

/**
 * Load and map a managed user's score profile.
 *
 * Execution flow
 * 1. Fetch raw managed-user profile payload.
 * 2. Map payload into frontend summary model.
 * 3. Return mapped summary for store/UI consumption.
 *
 * @param input Manager id + managed user id.
 * @returns Mapped managed-user summary.
 */
export const loadManagedUserProfile = async ({
  managerId,
  userId,
}: LoadManagedUserProfileInput): Promise<IManagedUserSummary> => {
  const apiManagedUserProfile = await getManagerUserProfile(managerId, userId);
  return mapApiManagedUserDataToFrontend(apiManagedUserProfile);
};
